import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { baseURL } from '../config';
import AuthContext from '../Context/AuthContext';
import Searchbar from './Searchbar';
import LoadingAnimation from './LoadingAnimation';
import { AccordionArrow } from './Icons';

const PAGE_SIZE = 50;

function AssemblySelector({ label, selected, onAdd, onRemove, maxSelect, excludeId, disabled }) {
    const { token, logout } = useContext(AuthContext);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [assemblyData, setAssemblyData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [fetchingAssemblyData, setFetchingAssemblyData] = useState(false);
    const [expandedType, setExpandedType] = useState(null);
    const observer = useRef();
    const debounceRef = useRef(null);
    const searchContainerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setSearchInput('');
                setSearch('');
                setPage(1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!search.trim()) {
            setAssemblyData([]);
            setTotalPages(1);
            return;
        }
        const fetchAssemblyData = async () => {
            try {
                setFetchingAssemblyData(true);
                const response = await axios.get(`${baseURL}/assembly/getAllAssemblies`, {
                    headers: { 'ngrok-skip-browser-warning': 'any', 'x-access-token': token },
                    params: { page, limit: PAGE_SIZE, search },
                });
                if (response?.status === 204) {
                    setAssemblyData([]);
                    setTotalPages(1);
                } else {
                    const results = (response.data.data.assemblyData || []).filter((item) => item._id !== excludeId);
                    if (page === 1) {
                        setAssemblyData(results);
                    } else {
                        setAssemblyData((prev) => [...prev, ...results]);
                    }
                    setTotalPages(response.data.data.pagination.totalPages);
                }
            } catch (error) {
                if (error.response?.status === 401) logout();
            } finally {
                setFetchingAssemblyData(false);
            }
        };
        fetchAssemblyData();
    }, [search, page, excludeId, token, logout]);

    const handleSearchChange = (value) => {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(value);
            setPage(1);
        }, 500);
    };

    const infiniteScrollRef = (node) => {
        if (fetchingAssemblyData) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && page < totalPages) {
                setPage((prev) => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    };

    const isSelected = (item) => selected.some((s) => s._id === item._id);

    const handleToggle = (item) => {
        if (disabled) return;
        if (isSelected(item)) {
            onRemove(item);
            return;
        }
        if (maxSelect === 1) {
            if (selected.length > 0) onRemove(selected[0]);
            onAdd(item);
            return;
        }
        if (selected.length >= maxSelect) {
            toast.error(`You can only add up to ${maxSelect} for ${label}`);
            return;
        }
        onAdd(item);
    };

    const toggleType = (type) => {
        setExpandedType((prev) => (prev === type ? null : type));
    };

    const linkedTypes = [...new Set(selected.map((s) => s.assembly_type).filter(Boolean))].sort();

    return (
        <div className='bg-white shadow-md rounded-lg p-4 md:p-6 mb-5'>
            <div className='flex justify-between items-center mb-4'>
                <p className='text-xl font-medium text-gray-700'>{label}</p>
                <p className='text-sm text-gray-500'>{selected.length} / {maxSelect} selected</p>
            </div>

            <div ref={searchContainerRef}>
                <p className='text-sm font-medium text-gray-500 mb-2'>Search assemblies</p>
                <Searchbar
                    fullWidth
                    value={searchInput}
                    disabled={disabled}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className='mb-2'
                />
                {search.trim() && (
                    <div className='border border-gray-200 rounded-md max-h-60 overflow-y-auto mb-4'>
                        {assemblyData.length === 0 && !fetchingAssemblyData && (
                            <p className='text-gray-400 p-3 text-sm'>{`No data found for '${search}'`}</p>
                        )}
                        {assemblyData.map((item, index) => (
                            <div
                                key={item._id}
                                ref={index === assemblyData.length - 1 ? infiniteScrollRef : null}
                                onClick={() => handleToggle(item)}
                                className='flex items-start gap-3 border-b border-dashed p-3 cursor-pointer hover:bg-slate-50'
                            >
                                <input
                                    type='checkbox'
                                    readOnly
                                    checked={isSelected(item)}
                                    disabled={disabled}
                                    className='mt-1 h-5 w-5 accent-orange-400 shrink-0'
                                />
                                <div className='min-w-0'>
                                    <p className='font-medium'>{item.assembly_number || item.model_number}</p>
                                    <p className='text-gray-400 text-sm truncate'>{item.assembly_description}</p>
                                </div>
                            </div>
                        ))}
                        {fetchingAssemblyData && <LoadingAnimation />}
                    </div>
                )}
            </div>

            <p className='text-sm font-medium text-gray-500 mb-2'>Linked {label}</p>
            <div className='border border-gray-200 rounded-md max-h-96 overflow-y-auto'>
                {selected.length === 0 && <p className='text-gray-400 p-3'>Nothing selected</p>}
                {linkedTypes.map((type) => {
                    const itemsOfType = selected.filter((s) => s.assembly_type === type);
                    const isOpen = expandedType === type;
                    return (
                        <div key={type} className='border-b border-dashed last:border-b-0'>
                            <button
                                type='button'
                                onClick={() => toggleType(type)}
                                className='w-full flex justify-between items-center gap-2 p-3 text-left hover:bg-slate-50'
                            >
                                <span className='font-medium'>{type}</span>
                                <span className='flex items-center gap-2 shrink-0'>
                                    <span className='text-sm text-gray-400'>{itemsOfType.length}</span>
                                    <span className={`transition-transform duration-200 ${!isOpen ? 'rotate-180' : ''}`}>
                                        <AccordionArrow />
                                    </span>
                                </span>
                            </button>
                            {isOpen && (
                                <div className='px-3 pb-3'>
                                    {itemsOfType.map((item) => (
                                        <div key={item._id} className='bg-slate-50 border border-slate-200 rounded-md p-3 mb-2 last:mb-0'>
                                            <div className='flex justify-between items-center gap-3'>
                                                <div className='min-w-0'>
                                                    <p className='font-medium text-sm'>{item.assembly_number || item.model_number}</p>
                                                    <p className='text-gray-400 text-sm truncate'>{item.assembly_description}</p>
                                                </div>
                                                <div className='flex items-center gap-4 shrink-0'>
                                                    <a href={`/assembly_detail/${item._id}`} target='_blank' rel='noopener noreferrer' className='text-orange-500 hover:text-orange-700 text-sm'>View</a>
                                                    <button type='button' disabled={disabled} onClick={() => onRemove(item)} className='text-red-500 hover:text-red-700 disabled:text-gray-300 text-sm'>Remove</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default AssemblySelector;
